/**
 * The Copilot transport — POST /api/platform/v1/copilot/ask, parsing its SSE events.
 *
 * A direct `fetch` rather than `apiClient`, because axios does not expose a
 * streaming response body. That means every header the interceptors would have
 * added has to be applied by hand here — and the one that is easy to forget is
 * `X-WeaveIn-Language`. Omitting it is silent: the server falls back to
 * `user_preferences.language_code`, which the in-app language switcher never
 * writes, so a Spanish-preference caseworker gets English answers inside a
 * Spanish UI and nothing errors. That exact omission is what made TRUS-446 take
 * two rounds to fix.
 *
 * Deliberately separate from `web-presence/lib/agentStream.ts`: that one drives
 * the design studio's orchestrator, sends `instruction`/`approved`, and does not
 * set the language header. Sharing it would mean one module serving two
 * different contracts.
 */
import { config } from '@/src/config';
import { UI_LANGUAGE_HEADER, currentUiLanguage } from '@/services/uiLanguageHeader';

/**
 * An action the Copilot wanted to take and did not.
 *
 * Everything the card renders comes from here rather than from a per-tool
 * lookup in the panel. A local table of descriptions drifts the moment a tool's
 * summary changes on the server, and the way it drifts is a card that describes
 * the wrong action — which is the one bug a confirmation dialog must not have.
 */
export interface PendingApproval {
  /**
   * The server's id for this offer, and the ONLY thing /confirm accepts.
   *
   * The arguments used to go back with it. That looked equivalent — this panel
   * sends what it displayed — but it left the server unable to tell a faithful
   * echo from an invented one, because it kept no record of what it had
   * offered. Now it does, and the card cannot misrepresent the action even if
   * this code is wrong.
   *
   * Optional only because the server omits it for agents it records nothing
   * for; a card without one cannot be confirmed and is not rendered.
   */
  id?: string;
  tool: string;
  /** Shown to the human. Not sent back — the server already has them. */
  args: Record<string, unknown>;
  gate?: string;
  reversibility?: 'read_only' | 'undoable' | 'compensable' | 'irreversible' | string;
  message?: string;
}

export type CopilotEvent =
  | { type: 'step'; tool: string; status?: string; ok?: boolean }
  | { type: 'summary'; summary: string; pending_approvals?: PendingApproval[]; conversation_id?: string | null }
  /* A tool's own output, for the client to render. The model gets the same
     data and is told not to retype it — a markdown table from a language model
     into a plain-text panel is a wall of pipes, and prose has no row to put a
     button on. */
  | { type: 'result'; tool: string; data: unknown }
  | { type: 'done' }
  | { type: 'error'; error: string };

/** What /confirm gives back: the tool's own result, plus its undo line. */
export interface CopilotActionResult {
  ok: boolean;
  status?: string;
  error?: string;
  undo?: string;
  [key: string]: unknown;
}

export interface CopilotTurn {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Which conversation a question continues.
 *
 * This replaced a `history` array this file used to send. It lived only in
 * React state, so a reload lost the conversation and nothing server-side had
 * ever recorded it — and the server could not trust it anyway: a
 * client-supplied tool result is an unverified claim about the caller's own
 * data, so it was stripped, and the agent could never see what its own tools
 * had returned. The server now owns the transcript and quotes itself.
 *
 * Absent on the first question of a conversation; the server opens one and
 * returns its id on the `summary` event.
 */
export type CopilotConversationId = string;

export interface CopilotCapabilities {
  enabled: boolean;
  language: string;
  tools: { name: string; group: string; summary: string }[];
  can_take_actions: boolean;
  /** Tools that will come back gated, so the panel need not hardcode the list. */
  action_tools?: string[];
  /** What THIS organization calls its assistant. Absent on an older backend;
   *  callers fall back to the platform default rather than to "Copilot". */
  assistant_name?: string;
  /** Whether to offer a microphone. Decided by the SERVER from the operator
   *  switch (`agent_registry.voice_mode`) and whether speech is configured at
   *  all — never inferred here. A client that decides for itself drifts the
   *  moment the switch moves, and the failure is a mic that connects to
   *  nothing. Absent on an older backend, which reads as off. */
  voice_enabled?: boolean;
}

/**
 * The Copilot lives under `/api/platform/v1`, not a bespoke `/api/copilot`.
 *
 * That is not a naming choice. `TenantMiddleware` on the backend only processes
 * paths starting `/api/platform` or `/api/customer-support`, and
 * `get_platform_user` reads the tenant context that middleware sets. Mounted
 * anywhere else, every request 401s for a signed-in user — valid token, passing
 * CORS preflight, "No tenant context resolved for this organization". That is
 * exactly how this shipped the first time.
 */
const COPILOT_BASE_PATH = '/api/platform/v1/copilot';

function apiBase(): string {
  return String((config as any)?.weaveinApi?.baseUrl || '').replace(/\/$/, '');
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    // Both portals: the member/employer apps store their JWT under a different
    // key, and the Copilot is reachable from either.
    const token = localStorage.getItem('auth_token') || localStorage.getItem('member_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const cbo = localStorage.getItem('active_cbo_id');
    if (cbo) headers['X-CBO-ID'] = cbo;
  } catch {
    /* localStorage unavailable (private window) — the request will 401 and the
       panel will say so, which is the correct outcome. */
  }
  const language = currentUiLanguage();
  if (language) headers[UI_LANGUAGE_HEADER] = language;
  return headers;
}

/** What the Copilot can do for this user, or null when it cannot be reached. */
export async function fetchCopilotCapabilities(
  signal?: AbortSignal,
): Promise<CopilotCapabilities | null> {
  try {
    const res = await fetch(`${apiBase()}${COPILOT_BASE_PATH}/capabilities`, {
      headers: authHeaders(),
      signal,
    });
    if (!res.ok) return null;
    return (await res.json()) as CopilotCapabilities;
  } catch {
    return null;
  }
}

/**
 * Stream one turn. Resolves when the stream ends; every event goes to `onEvent`.
 * Never throws — a transport failure arrives as an `error` event so the panel
 * has exactly one place to render failure.
 */
export async function askCopilot(
  body: { message: string; conversation_id?: CopilotConversationId | null; page_context?: string },
  onEvent: (ev: CopilotEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${COPILOT_BASE_PATH}/ask`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
      signal,
    });
  } catch (e: any) {
    if (e?.name === 'AbortError') return;
    onEvent({ type: 'error', error: 'Could not reach the Copilot.' });
    return;
  }

  if (!res.ok || !res.body) {
    onEvent({
      type: 'error',
      error: res.status === 401 ? 'Your session has expired. Sign in again.' : 'The Copilot is unavailable.',
    });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line. Keep the trailing partial
      // frame in the buffer — a chunk boundary lands mid-frame routinely, and
      // parsing half a JSON object would drop the event silently.
      const frames = buffer.split('\n\n');
      buffer = frames.pop() || '';
      for (const frame of frames) {
        const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
        if (!dataLine) continue;
        const payload = dataLine.slice(5).trim();
        if (!payload) continue;
        try {
          onEvent(JSON.parse(payload) as CopilotEvent);
        } catch {
          /* skip a malformed frame rather than ending the turn */
        }
      }
    }
  } catch (e: any) {
    if (e?.name !== 'AbortError') {
      onEvent({ type: 'error', error: 'The connection dropped mid-answer.' });
    }
  }
}

/**
 * Run one action the user confirmed.
 *
 * Sends the offer's id and nothing else. The server looks up what it proposed,
 * spends the id atomically so a double press cannot run the write twice, and
 * dispatches the arguments it stored — so the action that runs is the action
 * the card described, whatever this file does.
 *
 * There is deliberately no "approved" flag on /ask either: a boolean the client
 * sets would let any caller mark every request approved, and re-asking the
 * model to "do it now" cannot promise the call it makes is the call the human
 * read.
 *
 * Not a stream — there is no tool loop and no model call on this path, so there
 * is one request and one result.
 */
export async function confirmCopilotAction(
  actionId: string,
  signal?: AbortSignal,
): Promise<CopilotActionResult> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${COPILOT_BASE_PATH}/confirm`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ action_id: actionId }),
      signal,
    });
  } catch {
    return { ok: false, error: 'Could not reach the Copilot.' };
  }

  // The failure has to be legible, because the user just pressed a button that
  // said it would do something. "It did not work" with no reason is the version
  // of this that generates a support ticket.
  if (!res.ok) {
    let detail = '';
    try {
      detail = String(((await res.json()) as any)?.detail || '');
    } catch {
      /* a non-JSON error body is not worth failing over */
    }
    if (res.status === 403) return { ok: false, error: detail || 'You are not allowed to do that.' };
    if (res.status === 401) return { ok: false, error: 'Your session has expired. Sign in again.' };
    return { ok: false, error: detail || 'That did not go through.' };
  }

  try {
    return (await res.json()) as CopilotActionResult;
  } catch {
    return { ok: false, error: 'That did not go through.' };
  }
}

/** A minted voice session. `streaming` is false when speech is not configured. */
export interface CopilotVoiceSession {
  session_id: string;
  streaming: boolean;
}

/**
 * Mint a voice session bound to the signed-in caller.
 *
 * Note what this body does NOT carry: no org, no user, no agent key, no tool
 * list. The Triveny equivalent (`/agent-voice/browser-session`) takes a
 * `cbo_id` and prefers it over the caller's own — loose for a single-operator
 * product, org-hopping by construction for this one. The org and the actor come
 * from the verified session on the server.
 *
 * The id it returns is SINGLE-USE and short-lived: it travels in the websocket
 * URL, and a URL is not a good place for a credential.
 */
export async function createCopilotVoiceSession(
  lang?: string,
  signal?: AbortSignal,
): Promise<CopilotVoiceSession | null> {
  try {
    const res = await fetch(`${apiBase()}${COPILOT_BASE_PATH}/voice-session`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ lang: lang || currentUiLanguage() || 'en' }),
      signal,
    });
    if (!res.ok) return null;
    return (await res.json()) as CopilotVoiceSession;
  } catch {
    return null;
  }
}

/**
 * The websocket the minted session is used on.
 *
 * Absolute, from the configured API base — never `window.location.origin`. On
 * weavein.ai a same-origin path is caught by vercel.json's catch-all and
 * proxied to the DEV backend, so a relative URL here would open a voice session
 * against dev data while everything else in the panel talked to production.
 */
export function copilotVoiceSocketUrl(sessionId: string): string {
  return (
    apiBase().replace(/^http/, 'ws') +
    `/api/webhooks/voice-agent/browser-stream?session_id=${encodeURIComponent(sessionId)}`
  );
}

/**
 * Work the Copilot is doing that nobody has to sit and watch.
 *
 * `askCopilot` holds the connection open for the whole tool loop, which is right
 * for a question someone is waiting on and wrong for anything slower — the work
 * dies with the connection, and the panel can only have one in flight.
 *
 * A run is the other half. It returns an id immediately, a worker does the loop,
 * and the answer is somewhere to come back to. That is what lets one person have
 * several going at once.
 */
export interface CopilotRun {
  id: string;
  conversation_id: string | null;
  /** queued | running | awaiting_confirmation | done | failed | cancelled. */
  status: string;
  prompt: string;
  steps: { tool: string; status?: string; ok?: boolean }[];
  summary: string | null;
  error: string | null;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
}

/** Statuses that mean a worker still owes an answer. */
export const RUN_IS_OPEN = (status: string) => status === 'queued' || status === 'running';

export interface StartRunResult {
  run_id?: string;
  /** Set when the run could not be started, already legible. */
  error?: string;
}

/**
 * Queue a run. Returns as soon as the server has accepted it, not when it ends.
 *
 * A 429 means this person is already at their concurrent limit — a real answer,
 * not a failure, and worth saying plainly so they know to wait rather than
 * assuming the button is broken.
 */
export async function startCopilotRun(
  body: { message: string; conversation_id?: CopilotConversationId | null; page_context?: string },
  signal?: AbortSignal,
): Promise<StartRunResult> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${COPILOT_BASE_PATH}/runs`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
      signal,
    });
  } catch {
    return { error: 'Could not reach the Copilot.' };
  }

  if (!res.ok) {
    let detail = '';
    try {
      detail = String(((await res.json()) as any)?.detail || '');
    } catch {
      /* a non-JSON error body is not worth failing over */
    }
    if (res.status === 401) return { error: 'Your session has expired. Sign in again.' };
    return { error: detail || 'Could not start that right now.' };
  }

  try {
    const data = (await res.json()) as { run_id?: string };
    return { run_id: data.run_id };
  } catch {
    return { error: 'Could not start that right now.' };
  }
}

/** This user's runs, newest first. */
export async function fetchCopilotRuns(signal?: AbortSignal): Promise<CopilotRun[]> {
  try {
    const res = await fetch(`${apiBase()}${COPILOT_BASE_PATH}/runs?limit=20`, {
      headers: authHeaders(),
      signal,
    });
    if (!res.ok) return [];
    const data = (await res.json()) as { runs?: CopilotRun[] };
    return data.runs || [];
  } catch {
    // Including AbortError, which is the normal case on unmount.
    return [];
  }
}

/** Stop a run. False if it had already finished. */
export async function cancelCopilotRun(runId: string, signal?: AbortSignal): Promise<boolean> {
  try {
    const res = await fetch(`${apiBase()}${COPILOT_BASE_PATH}/runs/${encodeURIComponent(runId)}/cancel`, {
      method: 'POST',
      headers: authHeaders(),
      signal,
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * A past conversation, for the history list.
 *
 * `opening` is the person's own first message rather than a generated title —
 * it is what they will recognise, and it cannot drift from what the thread was
 * actually about.
 */
export interface CopilotConversation {
  id: string;
  surface: string;
  started_at: string | null;
  last_turn_at: string | null;
  /** Pinned threads sort first AND are exempt from the retention sweep. */
  pinned: boolean;
  /** Content expired. Only ever true for a pinned thread, which keeps its row. */
  purged: boolean;
  opening: string | null;
  message_count: number;
}

export interface CopilotStoredMessage {
  seq: number;
  role: 'user' | 'assistant';
  content: string;
  steps: { tool: string; status?: string; ok?: boolean }[];
  created_at: string | null;
}

/** This user's conversations, pinned first then most recent. */
export async function fetchCopilotConversations(signal?: AbortSignal): Promise<CopilotConversation[]> {
  try {
    const res = await fetch(`${apiBase()}${COPILOT_BASE_PATH}/conversations?limit=30`, {
      headers: authHeaders(),
      signal,
    });
    if (!res.ok) return [];
    const data = (await res.json()) as { conversations?: CopilotConversation[] };
    return data.conversations || [];
  } catch {
    // Including AbortError, which is the normal case on unmount.
    return [];
  }
}

/** Every message in one conversation, oldest first. */
export async function fetchCopilotConversation(
  conversationId: string,
  signal?: AbortSignal,
): Promise<CopilotStoredMessage[]> {
  try {
    const res = await fetch(
      `${apiBase()}${COPILOT_BASE_PATH}/conversations/${encodeURIComponent(conversationId)}`,
      { headers: authHeaders(), signal },
    );
    if (!res.ok) return [];
    const data = (await res.json()) as { messages?: CopilotStoredMessage[] };
    return data.messages || [];
  } catch {
    return [];
  }
}

/**
 * Pin or unpin. Returns the resulting state, or null if the request failed.
 *
 * Pinning is not only ordering: a pinned conversation is exempt from the
 * retention sweep, so this is how someone keeps a thread they do not want to
 * lose.
 */
export async function pinCopilotConversation(
  conversationId: string,
  pinned: boolean,
  signal?: AbortSignal,
): Promise<boolean | null> {
  try {
    const res = await fetch(
      `${apiBase()}${COPILOT_BASE_PATH}/conversations/${encodeURIComponent(conversationId)}/pin`,
      { method: 'POST', headers: authHeaders(), body: JSON.stringify({ pinned }), signal },
    );
    if (!res.ok) return null;
    const data = (await res.json()) as { pinned?: boolean };
    return Boolean(data.pinned);
  } catch {
    return null;
  }
}

/** A task as `task.list` returns it. */
export interface CopilotTask {
  id: number;
  title: string;
  status: string;
  priority: string;
  deadline: string | null;
  created_at: string | null;
}

export interface CopilotTaskListResult {
  member_id: number;
  count: number;
  tasks: CopilotTask[];
}

/**
 * Raise a confirmation card for an action the user CLICKED on a result row,
 * rather than typed for the model to interpret.
 *
 * The server attempts the tool unapproved through the same path a turn uses, so
 * this is not a shortcut around the gate: a caller who may not do it gets the
 * tool's own refusal, and what comes back on success is an ordinary card that
 * confirms by id like any other.
 */
export async function offerCopilotAction(
  tool: string,
  args: Record<string, unknown>,
  conversationId: string | null,
  signal?: AbortSignal,
): Promise<{ approval?: PendingApproval; error?: string }> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${COPILOT_BASE_PATH}/actions/offer`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ tool, args, conversation_id: conversationId }),
      signal,
    });
  } catch {
    return { error: 'Could not reach the Copilot.' };
  }

  if (!res.ok) {
    let detail = '';
    try {
      detail = String(((await res.json()) as any)?.detail || '');
    } catch {
      /* a non-JSON error body is not worth failing over */
    }
    return { error: detail || 'Could not start that.' };
  }

  try {
    const data = (await res.json()) as { approval?: PendingApproval };
    return { approval: data.approval };
  } catch {
    return { error: 'Could not start that.' };
  }
}

/** One step in a plan. `manual` is done by a person; `tool` the agent can run. */
export interface CopilotPlanStep {
  seq: number;
  kind: 'manual' | 'tool';
  instruction: string;
  page_key: string | null;
  tool: string | null;
  args: Record<string, unknown>;
  done: boolean;
}

export interface CopilotPlan {
  id: string;
  conversation_id: string | null;
  title: string;
  goal: string | null;
  status: 'open' | 'done' | 'abandoned';
  steps: CopilotPlanStep[];
}

/** What `plan.propose` returns: the plan, and how many steps were out of reach. */
export interface CopilotPlanResult {
  plan: CopilotPlan;
  steps_omitted: number;
}

/** Tick a step off. Returns the refreshed plan — completion is the server's call. */
export async function tickPlanStep(
  planId: string,
  seq: number,
  signal?: AbortSignal,
): Promise<CopilotPlan | null> {
  try {
    const res = await fetch(
      `${apiBase()}${COPILOT_BASE_PATH}/plans/${encodeURIComponent(planId)}/steps/${seq}/done`,
      { method: 'POST', headers: authHeaders(), signal },
    );
    if (!res.ok) return null;
    return (await res.json()) as CopilotPlan;
  } catch {
    return null;
  }
}

/**
 * A procedure this organization has learned, as an admin reviewing it sees it.
 *
 * `times_completed` and `approved` are the two kinds of evidence a procedure
 * can have. The review screen shows both because they mean different things:
 * repetition is weak evidence that something works, an admin's signature is
 * strong evidence that it SHOULD be how this is done.
 */
export interface CopilotProcedure {
  id: string;
  title: string;
  goal: string;
  steps: CopilotPlanStep[];
  times_completed: number;
  approved: boolean;
  retired: boolean;
  created_at: string | null;
  last_used_at: string | null;
}

export interface CopilotProcedureList {
  procedures: CopilotProcedure[];
  /** Completions needed before a procedure is suggested without approval. */
  min_uses: number;
}

/** Everything this org has learned. Admin-only; 403 for anyone else. */
export async function fetchCopilotProcedures(
  signal?: AbortSignal,
): Promise<CopilotProcedureList | { error: string }> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${COPILOT_BASE_PATH}/procedures`, { headers: authHeaders(), signal });
  } catch {
    return { error: 'Could not reach the Copilot.' };
  }
  if (res.status === 403) return { error: 'Only an administrator can review these.' };
  if (!res.ok) return { error: 'Could not load what the Copilot has learned.' };
  try {
    return (await res.json()) as CopilotProcedureList;
  } catch {
    return { error: 'Could not load what the Copilot has learned.' };
  }
}

/** Sign off a procedure so it is suggested before it has proven itself. */
export async function approveCopilotProcedure(id: string, signal?: AbortSignal): Promise<boolean> {
  try {
    const res = await fetch(
      `${apiBase()}${COPILOT_BASE_PATH}/procedures/${encodeURIComponent(id)}/approve`,
      { method: 'POST', headers: authHeaders(), signal },
    );
    return res.ok;
  } catch {
    return false;
  }
}

/** Stop a procedure being offered. The reason is kept for whoever asks later. */
export async function retireCopilotProcedure(
  id: string,
  reason?: string,
  signal?: AbortSignal,
): Promise<boolean> {
  try {
    const res = await fetch(
      `${apiBase()}${COPILOT_BASE_PATH}/procedures/${encodeURIComponent(id)}/retire`,
      { method: 'POST', headers: authHeaders(), body: JSON.stringify({ reason }), signal },
    );
    return res.ok;
  } catch {
    return false;
  }
}
